import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { flowRoutes, interleave } from "../../lib/reviewFlowRoutes.js";
import { ACTION_CHIP, FILE_LABEL } from "./triageParts.jsx";
import { CAPTION_H, STEP_INK, MET, useInks, alpha, useCanvas, useReveal, clientRect, hash, plural } from "./ReviewFlowParts.jsx";

// The Findings flow as a cascade: every finding (or every `unit` of them, said
// in the corner) is a grain poured onto the first ledge, oldest first. What
// the pipeline filtered out drops through its grate into the first basin;
// what it kept rolls off onto Triage's ledge, where what was cleared drops
// into the second. What needs a person rolls on into a well drawn magnified,
// one bead per finding, each row named. The piles are to scale.
//
// Every kept finding's grain is that finding: pointing at it names it and
// lights its row in the list below, and a click finds it there. Rows the list
// has on screen light their grains here. What was filtered out is a count
// only, so its grains are the basin's.

const REVEAL_MS = 3000;
const POUR_S = 1.5;
const GRAVITY = 1500;
const ROW_H = 16;
const INKS = { sand: STEP_INK.written, kept: STEP_INK.kept, triaged: STEP_INK.triaged, met: STEP_INK.met, muted: "var(--muted)", ink: "var(--ink)", line: "var(--line-strong)" };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (t) => t * t * (3 - 2 * t);
const fall = (dy) => Math.sqrt((2 * Math.max(0, dy)) / GRAVITY);
const isFiltered = (r) => r.kind === "drop" && r.stage === "kept";

// Slots in a basin: rows from the floor up, each row's columns in a shuffled
// order so a pile fills unevenly, as poured grains do.
function slots(n, x0, x1, floor, pitch, seed) {
  const per = Math.max(1, Math.floor((x1 - x0) / pitch) - 1);
  const perm = [...Array(per).keys()];
  for (let i = per - 1; i > 0; i--) { const j = Math.floor(hash(seed, i) * (i + 1)); [perm[i], perm[j]] = [perm[j], perm[i]]; }
  const out = [];
  for (let k = 0; k < n; k++) {
    const row = Math.floor(k / per);
    const col = perm[(k + row * 7) % per];
    out.push([x0 + (col + 0.5 + (row % 2) * 0.5) * pitch + pitch * 0.25, floor - (row + 0.5) * pitch * 0.9]);
  }
  return out;
}

function layoutCascade({ model, W, H }) {
  const { routes, unit, liftUnit } = flowRoutes(model);
  const g = { routes, unit, liftUnit, grains: [], rows: [], hits: [], counters: [], end: 1, more: 0 };
  if (!routes.length || !(W > 0)) return g;
  const hasWritten = (model.nodes.get("written")?.count || 0) > 0;
  const ys = 2;
  const yA = Math.round(H * 0.2);
  const yB = Math.round(H * 0.44);
  const floor = H - 4;
  const xA0 = W * 0.02;
  const xA1 = hasWritten ? W * 0.37 : W * 0.06;
  const gA0 = W * 0.085;
  const gA1 = W * 0.335;
  const bF0 = W * 0.065;
  const bF1 = W * 0.355;
  const bFt = yA + 12;
  const xB0 = xA1 + W * 0.015;
  const xB1 = W * 0.68;
  const gB0 = W * 0.445;
  const gB1 = W * 0.655;
  const bC0 = W * 0.425;
  const bC1 = W * 0.665;
  const bCt = yB + 12;
  const w0 = W * 0.705;
  const w1 = W - 6;
  const yW = yB + 14;
  Object.assign(g, { ys, yA, yB, floor, xA0, xA1, gA0, gA1, bF0, bF1, bFt, xB0, xB1, gB0, gB1, bC0, bC1, bCt, w0, w1, yW, hasWritten });

  const sequence = interleave(routes);
  const n = sequence.length;
  const nF = routes.filter(isFiltered).reduce((s, r) => s + r.marks, 0);
  const rest = routes.filter((r) => !isFiltered(r) && r.kind !== "lift");
  const nC = rest.reduce((s, r) => s + r.marks, 0);
  // The grain pitch: what fits the first basin half full, the same in both.
  const p = clamp(Math.sqrt(((bF1 - bF0) * (floor - bFt) * 0.5) / Math.max(1, nF, nC * 1.2)), 2.2, 7);
  const ps = Math.max(1.6, p * 0.66);
  const sF = slots(nF, bF0, bF1, floor, p, 7);
  const sC = slots(nC, bC0, bC1, floor, p, 11);

  // The well: a row per standard that flagged something, as the Sankey's last
  // column, its beads at the row's right end.
  const lifts = sequence.filter((m) => m.route.kind === "lift");
  const rowsFit = Math.max(1, Math.floor((floor - yW - 4) / ROW_H));
  const groups = new Map();
  sequence.forEach((m, i) => {
    if (m.route.kind !== "lift") return;
    if (!groups.has(m.route.id)) groups.set(m.route.id, { key: m.route.id, route: m.route, marks: [] });
    groups.get(m.route.id).marks.push(i);
  });
  const ordered = [...groups.values()];
  // A row gives way to the "more" line when they do not all fit.
  const shown = ordered.slice(0, ordered.length > rowsFit ? Math.max(1, rowsFit - 1) : rowsFit);
  g.more = ordered.length - shown.length;
  const rb = clamp(Math.min(ROW_H * 0.32, (w1 - w0) / 40), 2.5, 5);
  const beadAt = new Map();
  shown.forEach((G, k) => {
    const y = yW + 4 + k * ROW_H + ROW_H / 2;
    G.y = y;
    G.marks.forEach((i, j) => beadAt.set(i, [w1 - 8 - rb - j * rb * 2.6, y]));
    G.beadsX = w1 - 8 - G.marks.length * rb * 2.6;
    g.rows.push(G);
  });
  // Rows past what fits share the last row's slot.
  for (const G of ordered.slice(shown.length)) G.marks.forEach((i) => beadAt.set(i, beadAt.get(shown[shown.length - 1].marks[0])));

  let fi = 0;
  let ci = 0;
  g.grains = sequence.map(({ route: r }, k) => {
    const q = { route: r, i: k };
    q.ts = 0.08 + (POUR_S * k) / Math.max(1, n) + hash(k, 1) * 0.03;
    q.xs = xA0 + 6 + hash(k, 2) * 12;
    q.t1 = q.ts + fall(yA - ys);
    q.v = 420 + hash(k, 3) * 90;
    if (isFiltered(r)) {
      [q.sx, q.sy] = sF[fi++];
      q.xd = clamp(q.sx, gA0 + 3, gA1 - 3);
      q.t2 = q.t1 + Math.max(0, q.xd - q.xs) / q.v;
      q.t3 = q.t2 + fall(q.sy - yA);
    } else {
      q.t2 = q.t1 + (xA1 - q.xs) / q.v;
      const tf = fall(yB - yA);
      q.ve = (xB0 + 8 - xA1) / tf + hash(k, 4) * 30;
      q.xl = xA1 + q.ve * tf;
      q.t2b = q.t2 + tf;
      if (r.kind === "lift") {
        [q.sx, q.sy] = beadAt.get(k) || [w1 - 12, floor - 6];
        q.t3 = q.t2b + Math.max(0, xB1 - q.xl) / (q.v * 0.8);
        q.t4 = q.t3 + fall(q.sy - yB);
      } else {
        [q.sx, q.sy] = sC[ci++];
        q.xd = clamp(Math.max(q.sx, q.xl), gB0 + 3, gB1 - 3);
        q.t3 = q.t2b + Math.max(0, q.xd - q.xl) / q.v;
        q.t4 = q.t3 + fall(q.sy - yB);
      }
    }
    q.tEnd = isFiltered(r) ? q.t3 : q.t4;
    return q;
  });
  g.end = Math.max(1, ...g.grains.map((q) => q.tEnd));
  g.markTotals = { written: n, filtered: nF, kept: n - nF, cleared: routes.filter((r) => r.kind === "end").reduce((s2, r) => s2 + r.marks, 0), met: lifts.length };
  Object.assign(g, { p, ps, rb });

  // What the pointer can land on, and the counts that tick up as grains land.
  const hit = (id, x, top, w, h) => g.hits.push({ id, x, top, w, h });
  if (hasWritten) hit("written", xA0, 0, xA1 - xA0, yA + 6);
  if (nF) hit("filtered", bF0, bFt, bF1 - bF0, floor - bFt);
  hit(model.nodes.has("triaged") ? "triaged" : "kept", xB0, yB - 10, xB1 - xB0, 16);
  if (nC) hit(rest.find((r) => r.kind === "end")?.node || rest[0].node, bC0, bCt, bC1 - bC0, floor - bCt);
  if (lifts.length) hit("met", w0, yB - 10, w1 - w0, yW - yB + 10);
  const counter = (id, x, y, cls = "") => g.counters.push({ id, x, y, cls });
  if (hasWritten) counter("written", xA0 + 34, -2);
  if (nF) counter("filtered", bF0 + 8, bFt + 4);
  counter("kept", xB0 + 4, yB - 34, "kept");
  if (nC) counter("cleared", bC0 + 8, bCt + 4);
  if (lifts.length) counter("met", w0 + 4, yB - 22, "met");
  return g;
}

// A grain at time t: [x, y, phase, speed]; phase 9 is settled.
function grainAt(q, t, g) {
  if (t < q.ts) return null;
  if (t < q.t1) { const s = t - q.ts; return [q.xs, g.ys + 0.5 * GRAVITY * s * s, 0, GRAVITY * s]; }
  if (t < q.t2) return [lerp(q.xs, isFiltered(q.route) ? q.xd : g.xA1, (t - q.t1) / ((q.t2 - q.t1) || 1)), g.yA, 1, 0];
  if (isFiltered(q.route)) {
    if (t < q.t3) { const s = t - q.t2; return [lerp(q.xd, q.sx, smooth(s / (q.t3 - q.t2))), g.yA + 0.5 * GRAVITY * s * s, 2, GRAVITY * s]; }
    return [q.sx, q.sy, 9, 0];
  }
  if (t < q.t2b) { const s = t - q.t2; return [g.xA1 + q.ve * s, g.yA + 0.5 * GRAVITY * s * s, 2, GRAVITY * s]; }
  const lift = q.route.kind === "lift";
  if (t < q.t3) return [lerp(q.xl, lift ? g.xB1 : q.xd, (t - q.t2b) / ((q.t3 - q.t2b) || 1)), g.yB, 3, 0];
  if (t < q.t4) {
    const s = t - q.t3;
    const u = s / (q.t4 - q.t3);
    return [lift ? lerp(g.xB1, q.sx, u) : lerp(q.xd, q.sx, smooth(u)), g.yB + 0.5 * GRAVITY * s * s, 4, GRAVITY * s];
  }
  return [q.sx, q.sy, 9, 0];
}

function drawStructure(ctx, g, inks, pointed) {
  const { yA, yB, floor, xA0, xA1, gA0, gA1, bF0, bF1, bFt, xB0, xB1, gB0, gB1, bC0, bC1, bCt, w0, w1, yW } = g;
  ctx.lineWidth = 1;
  ctx.strokeStyle = alpha(inks.line, 1);
  ctx.beginPath();
  for (const [a, b, top] of [[bF0, bF1, bFt], [bC0, bC1, bCt]]) { ctx.moveTo(a + 0.5, top); ctx.lineTo(a + 0.5, floor + 0.5); ctx.lineTo(b + 0.5, floor + 0.5); ctx.lineTo(b + 0.5, top); }
  ctx.stroke();
  ctx.strokeStyle = alpha(inks.muted, 0.3);
  ctx.beginPath();
  for (const [a, b] of [[bF0, bF1], [bC0, bC1]]) for (let x = a + 12; x < b; x += 24) { ctx.moveTo(x, floor - 3); ctx.lineTo(x, floor); }
  ctx.stroke();
  if (pointed === "filtered") { ctx.fillStyle = alpha(inks.ink, 0.04); ctx.fillRect(bF0 + 1, bFt, bF1 - bF0 - 1, floor - bFt); }
  if (pointed === "cleared") { ctx.fillStyle = alpha(inks.triaged, 0.08); ctx.fillRect(bC0 + 1, bCt, bC1 - bC0 - 1, floor - bCt); }
  // Ledges: a heavy line with a dashed grate.
  const ledge = (x0, x1, ga, gb, y) => {
    ctx.strokeStyle = alpha(inks.ink, 0.6);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x0, y + 1); ctx.lineTo(ga, y + 1); ctx.moveTo(gb, y + 1); ctx.lineTo(x1, y + 1); ctx.lineTo(x1, y + 6); ctx.stroke();
    ctx.save();
    ctx.setLineDash([3, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = alpha(inks.muted, 0.9);
    ctx.beginPath(); ctx.moveTo(ga, y + 1); ctx.lineTo(gb, y + 1); ctx.stroke();
    ctx.restore();
  };
  if (g.hasWritten) ledge(xA0, xA1, gA0, gA1, yA);
  ledge(xB0, xB1, gB0, gB1, yB);
  // The spout.
  ctx.lineWidth = 1;
  ctx.strokeStyle = alpha(inks.sand, 0.95);
  ctx.beginPath(); ctx.moveTo(xA0 + 2, 0); ctx.lineTo(xA0 + 2, 6); ctx.moveTo(xA0 + 28, 0); ctx.lineTo(xA0 + 28, 6); ctx.stroke();
  if (!g.rows.length) return;
  // The well, drawn magnified.
  ctx.strokeStyle = alpha(inks.met, 0.7);
  ctx.beginPath(); ctx.moveTo(w0 + 0.5, yW); ctx.lineTo(w0 + 0.5, floor + 0.5); ctx.lineTo(w1 + 0.5, floor + 0.5); ctx.lineTo(w1 + 0.5, yW); ctx.stroke();
  ctx.beginPath();
  for (const x of [w0, w1]) { ctx.moveTo(x - 4, yW - 2); ctx.lineTo(x + 4, yW - 6); ctx.moveTo(x - 4, yW + 2); ctx.lineTo(x + 4, yW - 2); }
  ctx.stroke();
  ctx.fillStyle = alpha(inks.met, 0.045);
  ctx.fillRect(w0 + 1, yW, w1 - w0 - 1, floor - yW);
}

export default function ReviewFlowCascade({
  model, width, height, point = null, onPoint, onLeave, onOpen, caption, poolNote = "", onOpenLite = null,
}) {
  const boxRef = useRef(null);
  const g = useMemo(() => layoutCascade({ model, W: width, H: height }), [model, width, height]);
  const inks = useInks(boxRef, INKS);
  const [canvasRef, getCtx] = useCanvas(width, height);
  const [started, setStarted] = useState(false);
  const counterRefs = useRef(new Map());
  const pointed = point?.node || null;

  const paint = useCallback((p) => {
    const ctx = getCtx();
    if (!ctx || !inks || !g.grains.length) return;
    if (p > 0 && !started) setStarted(true);
    const t = p >= 1 ? Infinity : p * (g.end + 0.2);
    const dark = document.documentElement.dataset.theme === "dark";
    drawStructure(ctx, g, inks, pointed);
    const { ps, rb, yB, gB0 } = g;
    const counts = { written: 0, filtered: 0, kept: 0, cleared: 0, met: 0 };
    for (const q of g.grains) {
      const s = grainAt(q, t, g);
      if (!s) continue;
      counts.written += 1;
      const [x, y, ph, vy] = s;
      const landed = ph === 9;
      const r = q.route;
      if (isFiltered(r)) { if (landed) counts.filtered += 1; }
      else {
        if (ph >= 3 || landed) counts.kept += 1;
        if (landed && r.kind === "end") counts.cleared += 1;
        if (landed && r.kind === "lift") counts.met += 1;
      }
      let m = 1;
      if (pointed) m = r.path.includes(pointed) ? 1 : 0.25;
      if (r.kind === "lift" && ph >= 3) {
        const past = x > gB0;
        const grow = ph >= 4 ? smooth(clamp((y - yB) / 50, 0, 1)) : 0;
        const size = landed ? rb * 2 : lerp(ps, rb * 2, grow);
        if (dark) { ctx.shadowBlur = landed ? 14 : 8; ctx.shadowColor = alpha(inks.met, 0.8 * m); }
        ctx.fillStyle = past ? alpha(inks.met, m) : alpha(inks.kept, m);
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
        ctx.shadowBlur = 0;
        continue;
      }
      let col;
      let a = 1;
      if (isFiltered(r)) { col = ph >= 2 ? inks.muted : inks.sand; a = landed ? (dark ? 0.55 : 0.7) : 0.85; }
      else if (ph < 2) col = inks.sand;
      else if (landed && r.kind === "end") col = inks.triaged;
      else if (landed) { col = inks.muted; a = 0.6; }
      else col = inks.kept;
      ctx.fillStyle = alpha(col, a * m);
      if (vy > 80) { const len = Math.min(14, vy * 0.018); ctx.fillRect(x - ps / 2, y - len, ps, len + ps / 2); }
      else ctx.fillRect(x - ps / 2, y - ps / 2, ps, ps);
      if (landed && p < 1) {
        const lt = t - q.tEnd;
        if (lt < 0.25) { const rr = ps + lt * 26; ctx.strokeStyle = alpha(col, (1 - lt / 0.25) * 0.45); ctx.lineWidth = 1; ctx.strokeRect(x - rr / 2, y - rr / 2, rr, rr); }
      }
    }
    // The counts tick up as their grains land, to each node's own count.
    for (const [id, el] of counterRefs.current) {
      const total = model.nodes.get(id)?.count || 0;
      const shown = p >= 1 ? total : Math.round((counts[id] / Math.max(1, g.markTotals[id] || 0)) * total);
      const text = shown.toLocaleString();
      if (el.textContent !== text) el.textContent = text;
    }
  }, [g, inks, getCtx, started, pointed, model]);

  const signature = g.routes.length ? `${g.routes.map((r) => `${r.id}:${r.count}`).join(",")}` : "";
  const { done } = useReveal(inks ? signature : "", REVEAL_MS, paint);
  useLayoutEffect(() => { if (done.current) paint(1); }, [paint, done]);

  const hitAt = (x, y) => g.hits.find((h) => x >= h.x && x <= h.x + h.w && y >= h.top && y <= h.top + h.h) || null;
  const at = (e) => { const r = e.currentTarget.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const onMove = (e) => {
    const [x, y] = at(e);
    const h = hitAt(x, y);
    e.currentTarget.style.cursor = h ? "pointer" : "default";
    if (!h) { if (pointed) onLeave(); return; }
    if (h.id !== pointed) onPoint(h.id, clientRect(e.currentTarget, h.x, h.top, h.w, h.h));
  };
  const onOut = () => { if (pointed) onLeave(); };
  const onClick = (e) => {
    const [x, y] = at(e);
    const h = hitAt(x, y);
    if (h) onOpen(h.id);
  };

  const nodeOf = (id) => model.nodes.get(id);
  const point$ = (id) => (e) => onPoint(id, e.currentTarget.getBoundingClientRect());
  const triageEmpty = !model.nodes.has("triaged") && !model.nodes.has("waiting");
  const standardEmpty = !triageEmpty && !model.nodes.has("triaged");
  const hidden = !started;
  const top = CAPTION_H;
  // Spotted and Need you are named by the captions above them.
  const COUNTER_CAP = { written: "", filtered: "Filtered", kept: "Kept", cleared: "Cleared", met: "" };

  return (
    <div ref={boxRef} data-flow-view="cascade" className="absolute inset-0">
      {width > 0 && g.grains.length ? (
        <span data-flow-caption="written" className={`${FILE_LABEL} absolute top-0 whitespace-nowrap`} style={{ left: 0 }}>{caption(g.hasWritten ? "written" : "kept")}</span>
      ) : null}
      {width > 0 && g.rows.length ? (
        <span data-flow-caption="standard" className={`${FILE_LABEL} absolute top-0 whitespace-nowrap`} style={{ left: g.w0 }}>{caption("standard")}</span>
      ) : null}
      {g.unit > 1 ? (
        <span data-flow-unit="" className="absolute right-0 text-[9px] leading-[1.4] tracking-[0.02em] text-[color:var(--muted-soft)]" style={{ top: 0, left: g.w0 > 0 ? undefined : 0 }}>
          each grain is {g.unit.toLocaleString()} findings{g.liftUnit === 1 ? "; what needs you, one each" : `; what needs you, ${g.liftUnit.toLocaleString()} each`}
        </span>
      ) : null}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute left-0"
        style={{ top, width, height }}
        onPointerMove={onMove}
        onPointerLeave={onOut}
        onClick={onClick}
      />
      {width > 0 ? g.counters.map((c) => {
        const node = nodeOf(c.id);
        if (!node) return null;
        const dim = Boolean(pointed) && pointed !== c.id && !g.routes.some((r) => r.path.includes(pointed) && r.path.includes(c.id));
        return (
          <button
            key={c.id}
            type="button"
            data-flow-node={c.id}
            aria-label={`${node.label}: ${plural(node.count, "finding")}`}
            onMouseEnter={point$(c.id)}
            onMouseLeave={onLeave}
            onFocus={point$(c.id)}
            onBlur={onLeave}
            onClick={() => onOpen(c.id)}
            className={`absolute grid cursor-pointer gap-[2px] rounded-[2px] border-0 bg-transparent p-0 text-left transition-opacity ${dim ? "opacity-40" : ""}`}
            style={{ left: c.x, top: top + c.y }}
          >
            {COUNTER_CAP[c.id] ? <span className="text-[9px] leading-[1.2] tracking-[0.08em] uppercase" style={{ color: c.cls === "kept" ? "var(--warn, #c08a2d)" : "var(--muted-soft)" }}>{COUNTER_CAP[c.id]}</span> : null}
            <span ref={(el) => { if (el) counterRefs.current.set(c.id, el); else counterRefs.current.delete(c.id); }} className="text-[15px] leading-[1.1] font-medium tabular-nums" style={{ color: c.cls === "met" ? MET : "var(--ink)" }}>0</span>
          </button>
        );
      }) : null}
      {width > 0 ? g.rows.map((G, k) => {
        const node = nodeOf(G.route.node);
        const dim = Boolean(pointed) && pointed !== G.route.node && pointed !== "met" && !G.route.path.includes(pointed);
        return (
          <button
            key={G.key}
            type="button"
            data-flow-node={G.route.node}
            aria-label={node ? `${node.label}: ${plural(node.count, "finding")}` : undefined}
            onMouseEnter={point$(G.route.node)}
            onMouseLeave={onLeave}
            onFocus={point$(G.route.node)}
            onBlur={onLeave}
            onClick={() => onOpen(G.route.node)}
            className={`absolute flex cursor-pointer items-center gap-[6px] overflow-hidden border-0 bg-transparent p-0 text-left whitespace-nowrap transition-opacity duration-500 ${hidden ? "opacity-0" : dim ? "opacity-40" : ""}`}
            style={{ left: g.w0 + 8, top: top + G.y - ROW_H / 2, height: ROW_H, width: Math.max(40, G.beadsX - g.w0 - 14), transitionDelay: `${Math.round(REVEAL_MS * 0.75) + k * 60}ms` }}
          >
            <span className="shrink-0 text-[12px] font-medium tabular-nums" style={{ color: MET }}>{(node?.count ?? G.marks.length).toLocaleString()}</span>
            <span className="truncate text-[11px] text-[color:var(--ink)]">{node?.label || "Needs you"}</span>
          </button>
        );
      }) : null}
      {width > 0 && g.more > 0 ? (
        <span className="absolute text-[10px] text-[color:var(--muted)]" style={{ left: g.w0 + 8, top: top + height - 14 }}>+{g.more} more standards</span>
      ) : null}
      {width > 0 && triageEmpty && poolNote ? (
        <span data-flow-note="pool" className={`${NOTE_CLASS} absolute`} style={{ left: g.xB0 + 10, maxWidth: Math.max(120, width - g.xB0 - 10), top: top + height / 2 }}>{poolNote}</span>
      ) : null}
      {width > 0 && standardEmpty ? (
        <span data-flow-note="untriaged" className="absolute grid justify-items-start gap-[6px]" style={{ left: g.w0, maxWidth: 220, top: top + height / 2 - 10 }}>
          <span className={NOTE_CLASS}>Triage has not checked any of these yet.</span>
          {onOpenLite ? <button type="button" data-flow-open="lite" onClick={onOpenLite} className={ACTION_CHIP}>Open Triage</button> : null}
        </span>
      ) : null}
    </div>
  );
}
